import React, { useState, useEffect } from 'react';
import Loader from '../Common/Loader';
import { getStats, getCourseStats, getCourses } from '../../services/adminService';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import CourseStatsView from './CourseStatsView';

const ProgressView = () => {
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseStats, setCourseStats] = useState({}); // { [courseId]: statsData }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, coursesRes] = await Promise.all([getStats(), getCourses()]);
        const cs = coursesRes.data || [];
        setStats(statsRes.data);
        setCourses(cs);
        const pairs = await Promise.all(
          cs.map(c => getCourseStats(c.id).then(r => [c.id, r.data]).catch(() => [c.id, null]))
        );
        const map = {};
        pairs.forEach(([id, d]) => { map[id] = d; });
        setCourseStats(map);
      } catch {
        toast.error('Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Кнопка экспорта убрана с экрана, но выгрузка оставлена — вернуть можно одной строкой
  // eslint-disable-next-line no-unused-vars
  const exportToExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const usedNames = new Set();
      const safeSheet = (name) => {
        let n = (name || 'Лист').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 28) || 'Лист';
        let base = n, i = 2;
        while (usedNames.has(n)) { n = `${base.slice(0, 25)} ${i++}`; }
        usedNames.add(n);
        return n;
      };
      const addSheet = (name, rows) => {
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws['!cols'] = rows[0]?.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, ws, safeSheet(name));
      };

      addSheet('Общая статистика', [
        ['Показатель', 'Значение'],
        ['Всего учеников',          stats?.totalStudents || 0],
        ['Активных курсов',         stats?.activeCourses || 0],
        ['Всего уроков',            stats?.totalLessons || 0],
        ['Новых учеников (неделя)',  stats?.newStudents || 0],
        ['Средний прогресс',        `${stats?.averageProgress || 0}%`],
      ]);

      // Лист «По курсам» — строка на каждый курс
      addSheet('По курсам', [
        ['Курс', 'Экзамен', 'Учеников', 'Уроков', 'Средний прогресс'],
        ...courses.map(c => {
          const d = courseStats[c.id] || {};
          return [c.name, c.examType || '—', d.totalStudents || 0, d.totalLessons || 0, `${d.averageProgress || 0}%`];
        }),
      ]);

      // По листу «Уроки — {курс}» на каждый курс с данными
      courses.forEach(c => {
        const ls = courseStats[c.id]?.lessonStats;
        if (ls?.length) {
          addSheet(`Уроки — ${c.name}`, [
            ['Урок', 'Раздел', 'Сдали', 'Не сдали', 'Прогресс'],
            ...ls.map(l => [
              l.title, l.section,
              `${l.completed} / ${l.totalStudents}`,
              `${l.notCompleted} / ${l.totalStudents}`,
              `${l.totalStudents > 0 ? Math.round((l.completed / l.totalStudents) * 100) : 0}%`,
            ]),
          ]);
        }
      });

      const d = new Date();
      XLSX.writeFile(wb, `статистика_${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()}.xlsx`);
      toast.success('Отчёт экспортирован');
    } catch {
      toast.error('Ошибка при экспорте');
    }
  };

  return (
    <div>
      <div className="st-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="s-page-title">Статистика</h1>
          <p className="s-page-sub">Прогресс по модулям, урокам и ученикам</p>
        </div>
      </div>

      {loading ? <Loader /> : <CourseStatsView courses={courses} />}
    </div>
  );
};

export default ProgressView;
